import React from 'react';
import { Card, Row, Col, Form, Badge } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { numericKeydownHandler } from '../../../utilities/common';
import { formatNumber } from '../../reports/reportHelper';
import { formatCo2eFactor } from '../scenarioConfig';

const HvacOptimizationForm = ({ baseline, mode }) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const energy = baseline?.data?.energy;

  return (
    <>
      <Card className="mb-4">
        <Card.Body>
          <div className="font-weight-bold mb-3">
            {t('CarbonView Data')}
          </div>

          <Row>
            <Col md={4}>
              <div className="text-muted small">
                {t('HVAC Energy Use')}
              </div>
              <div className="font-weight-medium">
                {energy
                  ? `${formatNumber(energy.consumption)} ${energy.unit ?? ''}/year`
                  : '...'}
              </div>
            </Col>

            <Col md={4}>
              <div className="text-muted small">
                {t('Emission Factor')}
              </div>
              <div className="font-weight-medium">
                {energy
                  ? `${formatCo2eFactor(energy.emissionFactor)} tCO₂e/${energy.unit ?? ''}`
                  : '...'}
              </div>
            </Col>

            <Col md={4}>
              <div className="text-muted small">
                {t('Fuel Type')}
              </div>
              <div className="font-weight-medium">
                {t('Electric')}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ================= User Parameters ================= */}
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="font-weight-bold">
              {t('User Parameters')}
            </div>
            <Badge variant="success">
              {t('Required')}
            </Badge>
          </div>

          <div className="text-muted small mb-4">
            {t('Define efficiency improvements and costs')}
          </div>

          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Efficiency Improvement (%)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 15')}
                  {...register('reductionPercent')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.reductionPercent}
                />
                <Form.Text className="text-muted">
                  {t('Expected efficiency gain percentage')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.reductionPercent?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Implementation Cost (£)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 45000')}
                  disabled={mode === 'view'}
                  {...register('investmentCost')}
                  isInvalid={!!errors.investmentCost}
                />
                <Form.Text className="text-muted">
                  {t('Total cost of HVAC upgrades')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.investmentCost?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>
                  {t('Electricity Tariff Rate (£/kWh)')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  onKeyDown={numericKeydownHandler}
                  placeholder={t('e.g., 0.18')}
                  {...register('tariffRate')}
                  disabled={mode === 'view'}
                  isInvalid={!!errors.tariffRate}
                />
                <Form.Text className="text-muted">
                  {t('Current electricity cost per kWh')}
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.tariffRate?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default HvacOptimizationForm;
